import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getAssetUrl } from '../../lib/utils';
import { adminApiClient } from '../../lib/api';

interface MediaManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    templeId: string;
    initialData: {
        logo?: string;
        banner?: string;
        images?: string[];
    };
    onSuccess: () => void;
}

export const MediaManagementModal: React.FC<MediaManagementModalProps> = ({
    isOpen,
    onClose,
    templeId,
    initialData,
    onSuccess
}) => {
    const [logo, setLogo] = useState<string | null>(initialData.logo || null);
    const [banner, setBanner] = useState<string | null>(initialData.banner || null);
    const [gallery, setGallery] = useState<string[]>(initialData.images || []);

    const [newLogo, setNewLogo] = useState<File | null>(null);
    const [newBanner, setNewBanner] = useState<File | null>(null);
    const [newGalleryImages, setNewGalleryImages] = useState<File[]>([]);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setLogo(initialData.logo || null);
            setBanner(initialData.banner || null);
            setGallery(initialData.images || []);
            setNewLogo(null);
            setNewBanner(null);
            setNewGalleryImages([]);
        }
    }, [isOpen, initialData]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'banner' | 'gallery') => {
        if (!e.target.files?.length) return;

        if (type === 'logo') {
            setNewLogo(e.target.files[0]);
        } else if (type === 'banner') {
            setNewBanner(e.target.files[0]);
        } else if (type === 'gallery') {
            setNewGalleryImages(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const handleRemoveNewGallery = (index: number) => {
        setNewGalleryImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteExisting = async (type: 'logo' | 'banner' | 'gallery', value: string) => {
        try {
            // For now, we mainly handle gallery deletion visually, then API handles persisted state.
            // But since updateTempleWithImages is additive for images usually, strict deletion might need a separate call or specific logic.
            // However, our task is "add one icon for add media... update form".
            // Direct deletion of existing assets usually requires a delete endpoint.
            // For MVP, we'll mark them as removed locally? No, that won't delete from server.
            // We'll call deleteTempleImage for gallery.
            // For logo/banner, replacing them is uploading new one. Deleting them? Optional.

            if (type === 'gallery') {
                if (window.confirm('Are you sure you want to delete this image?')) {
                    await adminApiClient.deleteTempleImage(templeId, value);
                    setGallery(prev => prev.filter(img => img !== value));
                    toast.success('Image deleted');
                }
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete image');
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const formData = new FormData();

            if (newLogo) formData.append('logoFile', newLogo);
            if (newBanner) formData.append('bannerFile', newBanner);
            newGalleryImages.forEach(file => formData.append('images', file));

            if (!newLogo && !newBanner && newGalleryImages.length === 0) {
                toast('No new changes to save');
                onClose();
                return;
            }

            const response = await adminApiClient.updateTempleWithImages(templeId, formData);
            if (response.success) {
                toast.success('Media updated successfully');
                onSuccess();
                onClose();
            } else {
                toast.error('Failed to update media');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error updating media');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-semibold text-gray-900">Manage Temple Media</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 rounded-full p-2 hover:bg-gray-100">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-8">
                    {/* Logo Section */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">Temple Logo</h3>
                            <label className="cursor-pointer bg-white px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">
                                <span className="flex items-center gap-2">
                                    <Upload className="w-4 h-4" />
                                    Change Logo
                                </span>
                                <input type="file" className="hidden" accept="image/*" onChange={e => handleFileChange(e, 'logo')} />
                            </label>
                        </div>
                        <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                            <div className="w-24 h-24 rounded-full bg-white shadow-sm flex items-center justify-center overflow-hidden border">
                                {(newLogo || logo) ? (
                                    <img
                                        src={newLogo ? URL.createObjectURL(newLogo) : getAssetUrl(logo!)}
                                        alt="Logo"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <ImageIcon className="w-8 h-8 text-gray-400" />
                                )}
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">Recommended Size</p>
                                <p className="text-sm text-gray-500">512x512px, PNG or JPG</p>
                                {newLogo && <p className="text-sm text-green-600 mt-1">New file selected: {newLogo.name}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Banner Section */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">Banner Image</h3>
                            <label className="cursor-pointer bg-white px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">
                                <span className="flex items-center gap-2">
                                    <Upload className="w-4 h-4" />
                                    Change Banner
                                </span>
                                <input type="file" className="hidden" accept="image/*" onChange={e => handleFileChange(e, 'banner')} />
                            </label>
                        </div>
                        <div className="aspect-[21/9] bg-gray-100 rounded-lg overflow-hidden border relative group">
                            {(newBanner || banner) ? (
                                <img
                                    src={newBanner ? URL.createObjectURL(newBanner) : getAssetUrl(banner!)}
                                    alt="Banner"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <span>No Banner Image</span>
                                </div>
                            )}
                            {newBanner && (
                                <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                                    New: {newBanner.name}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Gallery Section */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">Gallery ({gallery.length + newGalleryImages.length} Images)</h3>
                            <label className="cursor-pointer bg-indigo-600 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700">
                                <span className="flex items-center gap-2">
                                    <Upload className="w-4 h-4" />
                                    Add Images
                                </span>
                                <input type="file" className="hidden" multiple accept="image/*" onChange={e => handleFileChange(e, 'gallery')} />
                            </label>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {gallery.map((img, idx) => (
                                <div key={idx} className="relative aspect-square group rounded-lg overflow-hidden border bg-gray-100">
                                    <img src={getAssetUrl(img)} alt={`Gallery ${idx}`} className="w-full h-full object-cover" />
                                    <button
                                        onClick={() => handleDeleteExisting('gallery', img)}
                                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            {newGalleryImages.map((file, idx) => (
                                <div key={`new-${idx}`} className="relative aspect-square group rounded-lg overflow-hidden border bg-gray-50 border-indigo-200">
                                    <img src={URL.createObjectURL(file)} alt="New upload" className="w-full h-full object-cover opacity-80" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">New</span>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveNewGallery(idx)}
                                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 sticky bottom-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};
